import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery } from '@tanstack/react-query';

import { TournamentOverview, TournamentRequest } from '~/server/src/dtos';

import { getFn, MessageResponse, postFn, queryClient, RequestError } from './api';

export const tournaments = createQueryKeys('tournaments', {
  all: null,
  detail: (tournamentId: string) => ({ queryKey: [tournamentId] }),
});

export const getTournaments = `/tournaments`;
export const getTournament = (tournamentid: string) => `${getTournaments}/${tournamentid}`;
export const postTournament = getTournaments;

export const useTournaments = (
  options?: Omit<
    Parameters<typeof useQuery<TournamentOverview[], RequestError>>[0],
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<TournamentOverview[], RequestError>({
    ...tournaments.all,
    queryFn: ({ signal }) => getFn(getTournaments, undefined, undefined, { signal }),
    ...options,
  });
};

export const useTournament = (
  tournamentId = '',
  options?: Omit<
    Parameters<typeof useQuery<TournamentOverview, RequestError>>[0],
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<TournamentOverview, RequestError>({
    ...tournaments.detail(tournamentId),
    queryFn: ({ signal }) => getFn(getTournament(tournamentId), undefined, undefined, { signal }),
    ...options,
    enabled: !!tournamentId && (options?.enabled === undefined || options.enabled === true),
  });
};

export const useCreateTournament = (
  options?: Parameters<typeof useMutation<MessageResponse, RequestError, TournamentRequest>>[0],
) => {
  return useMutation<MessageResponse, RequestError, TournamentRequest>({
    mutationFn: (data) => postFn(postTournament, data),
    ...options,
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ queryKey: tournaments.all.queryKey });
      options?.onSuccess?.(...response);
    },
  });
};
